<script lang="ts" setup>
    import { onMounted, ref, watch, reactive, computed} from 'vue';
    import * as AppointmentModel from '../../lib/Models/Schedule/Schedule';
    import vSelect from 'vue-select'; 
    import TextInput from "../../components/TextInput.vue"
    import * as _ from 'lodash';
    import { PageTitle } from '../../LibMain';
    import DateTimePickerNoTZ from '../../components/Inputs/DateTimePickerNoTZ.vue';
    import { addMinutes } from '../../lib/DateUtils';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import { ActionButtonStyle } from '../../lib/Types/Types'; 
    import { popAlert } from '../../lib/Notification';
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import { AppointmentState, CompanySettingParameterNames } from '../../Enums/ChoiceEnums';
    import * as SearchModel from '../../lib/Models/SearchModel';
    import { ParameterValueTypes } from '../../lib/Api/CompanySettings';

    import '../../../css/choice.css';
    import 'vue-select/dist/vue-select.css';

    const props = defineProps<{
        screenName: string,
        appointmentState: number,
        patientEntityId?: number, 
        appointmentId?: number,
        lookupAppointmentTypes: AppointmentModel.IAppointmentTypeItem[],
        lookupServiceLocations: AppointmentModel.IServiceLocationItem[],
        appointmentInfo: AppointmentModel.IAppointmentInfoByAppointmentId,
        authorizationInfo?: AppointmentModel.IAuthorizationsByAppointmentId[],
        lookupInsuranceProfiles: AppointmentModel.IInsuranceProfileListItem[],
        defaultComplaint?: string,
        defaultComment?: string
    }>();

    const apptData = reactive<{        
        appointmentTypeId?: number,
        serviceLocationId?: number,
        chiefComplaint?: string,
        comment?: string,
        insuranceProfileInfo?: AppointmentModel.IInsuranceProfileResults,
        insuranceProfileDefault?: AppointmentModel.IInsuranceProfileDefault,
        insuranceProfileList?: AppointmentModel.IInsuranceProfileItem[]
        currentInsuranceProfileId?: number,
        currentPrimaryInsurancePayerId?: number,
        currentSecondaryInsurancePayerId?: number,
        currentTertiaryInsurancePayerId?: number,
        currentReferringProviderEntityId?: number,
        referringProviderList?: AppointmentModel.IProviderListItem[],
        providerSearchTerm?: any,
        priorAuthorizationNumber?: string,
        assignAuthorizationId?: number,
        showMessage: boolean,
        saveMessageText: string,
        selectedReferringProvider?: AppointmentModel.IProviderListItem,
        currentPatientEntityId?: number,
        patientList?: SearchModel.ISearchPatientResults,
        selectedPatient?: SearchModel.ISearchPatientItem,
        patientSearchTerm?: any,
        currentResourceId?: number,
        resourceList?: SearchModel.ISearchResourceResults,
        selectedResource?: SearchModel.ISearchResourceItem,
        resourceSearchTerm?: any,
        apptStartDateTime?: string,
        apptEndDateTime?: string,
        appointmentTypeList?: AppointmentModel.IAppointmentTypeItem[],
        selectedAppointmentType?: AppointmentModel.IAppointmentTypeItem,
        insuranceProfileLookupList?: AppointmentModel.IInsuranceProfileListItem[],
        selectedInsuranceProfile?: AppointmentModel.IInsuranceProfileListItem,
        selectedPrimaryInsurance?: AppointmentModel.IInsuranceProfileItem,
        selectedSecondaryInsurance?: AppointmentModel.IInsuranceProfileItem,
        selectedTertiaryInsurance?: AppointmentModel.IInsuranceProfileItem,
        resourceAppointmentTypeList?: AppointmentModel.IAppointmentTypeItem[],
        companySettingOverridePlaceholderAppointmentType: boolean,
        companySettingOverridePlaceholderServiceLocation: boolean,
        companySettingServiceLocationRequired: boolean
    }>({        
        appointmentTypeId: props.appointmentInfo.appointmentTypeId,
        serviceLocationId: props.appointmentInfo.serviceLocationId,
        chiefComplaint: props.appointmentInfo.chiefComplaint,
        comment: props.appointmentInfo.appointmentComment,
        currentReferringProviderEntityId: props.appointmentInfo.referringProviderEntityId,   
        currentPatientEntityId: props.patientEntityId,   
        currentResourceId: props.appointmentInfo.resourceId,  
        priorAuthorizationNumber: props.appointmentInfo.priorAuthNumber,
        providerSearchTerm: props.appointmentInfo.referringProviderName?.substring(0, 3),
        showMessage: false,
        saveMessageText: "Appointment saved successfully",
        apptStartDateTime: props.appointmentInfo.apptStartDateTime?.toString(),
        apptEndDateTime: props.appointmentInfo.apptEndDateTime?.toString(),
        appointmentTypeList: props.lookupAppointmentTypes,
        insuranceProfileLookupList: props.lookupInsuranceProfiles,
        companySettingOverridePlaceholderAppointmentType: false,
        companySettingOverridePlaceholderServiceLocation: false,
        companySettingServiceLocationRequired: true
    });

    onMounted(async () => {
        await loadInsuranceProfileInfo(); 

        await Promise.all([            
            loadReferringProviderList(),
            loadPatientList(),
            loadResourceList(),
            updateSelections(),
            loadResourceAppointmentTypeList(),
            loadCompanySettings()
        ]);
    }); 

    watch(() => apptData.chiefComplaint, (newVal) => {
        if (txtComplaintRef.value) {
            txtComplaintRef.value.value = newVal ?? '';
        }
    });

    watch(() => apptData.comment, (newVal) => {
        if (txtCommentRef.value) {
            txtCommentRef.value.value = newVal ?? '';
        }
    });

    watch(() => apptData.selectedAppointmentType, async (newVal) => {
        await updateApptType();
    });    

    const txtComplaintRef = ref<HTMLTextAreaElement | null>(null);
    const txtCommentRef = ref<HTMLTextAreaElement | null>(null);    

    watch(() => props.defaultComplaint, (newValue, oldValue) => {
        apptData.chiefComplaint = newValue;
    });

    watch(() => props.defaultComment, (newValue, oldValue) => {
        apptData.comment = newValue;
    });

    const ApptPageTitle = computed<string>(() => {        
        return props.appointmentState == AppointmentState.Edit ? "Edit Appointment" : "New Appointment";
    }); 
    
    const AppointmentTypeTitle = computed<string>(() => {        
        return apptData.companySettingOverridePlaceholderAppointmentType ? "Select Appointment Type" : "Due to your company setting (Override Placeholder Appt Type), you cannot change the appointment type";
    }); 

    const ServiceLocationTitle = computed<string>(() => {        
        return apptData.companySettingOverridePlaceholderServiceLocation ? "Select Service Location" : "Due to your company setting (Override Placeholder Service Location), you cannot change the location";
    }); 

    const disableSaveButton = computed<boolean>(() => {        
        return (!apptData.selectedAppointmentType || !apptData.selectedInsuranceProfile || !apptData.selectedPatient || !apptData.selectedResource
            || !apptData.apptStartDateTime || !apptData.apptEndDateTime || (!apptData.serviceLocationId && apptData.companySettingServiceLocationRequired));
    });

    const disableSearch = computed<boolean>(() => {        
        return (props.appointmentState == AppointmentState.Edit);
    });

    const formattedAuthorizationList = computed(() =>
        props.authorizationInfo.map((auth: any) => {
            const newList: string[] = [];

            // Always include # sign prefix even if the number is empty/null
            const authNum = auth.authorizationNumber?.trim() || '';
            newList.push(`#${authNum}`);

            if (auth.coverageLevel?.trim()) {
                let coverage = auth.coverageLevel.trim();

                // Combine coverage and payerName if payerName exists
                if (auth.payerName?.trim()) {
                    coverage += `: ${auth.payerName.trim()}`;
                }

                newList.push(coverage);
            }

            if (auth.authorizationReason?.trim()) {
                newList.push(auth.authorizationReason.trim());
            }

            return {
                ...auth,
                authorizationDesc: newList.join('; ')
            };
        })
    );

    async function loadCompanySettings() {
        //get Override Placeholder Appointment Type
        const respType = await ChoiceApi.companySettings.getCompanySetting(
            { 
                parameterName: CompanySettingParameterNames.Scheduler,
                parameterOrder: 20,
                valueType: ParameterValueTypes.Boolean
            }
        );        
        apptData.companySettingOverridePlaceholderAppointmentType = respType?.parameterValue === "true";  

        //get Override Placeholder Service Location
        const respLoc = await ChoiceApi.companySettings.getCompanySetting(
            { 
                parameterName: CompanySettingParameterNames.Scheduler,
                parameterOrder: 21,
                valueType: ParameterValueTypes.Boolean
            }
        );        
        apptData.companySettingOverridePlaceholderServiceLocation = respLoc?.parameterValue === "true";

        //get Service Location Required
        const respReq = await ChoiceApi.companySettings.getCompanySetting(
            { 
                parameterName: CompanySettingParameterNames.ServiceLocationReq,
                parameterOrder: 1,
                valueType: ParameterValueTypes.Boolean
            }
        );        
        apptData.companySettingServiceLocationRequired = respReq?.parameterValue === "true";
    }

    async function updateSelections() {
        apptData.assignAuthorizationId = props.appointmentInfo.authorizationId;
        apptData.selectedAppointmentType = apptData.appointmentTypeList?.find(apptType => apptType.appointmentTypeId === apptData.appointmentTypeId);

        apptData.insuranceProfileLookupList = apptData.insuranceProfileLookupList?.map(profile => ({
            ...profile,
            inUse: apptData.insuranceProfileList?.some(used => used.insuranceProfileId === profile.insuranceProfileId)
        }));
        apptData.selectedInsuranceProfile = apptData.insuranceProfileLookupList?.find(profile => profile.insuranceProfileId === apptData.currentInsuranceProfileId);

        apptData.selectedPrimaryInsurance = apptData.insuranceProfileList?.find(payerId => payerId.insuredId === apptData.currentPrimaryInsurancePayerId);

        apptData.insuranceProfileList = apptData.insuranceProfileList?.map(profile => {
        const match = apptData.insuranceProfileLookupList?.find(
            lookup => lookup.insuranceProfileId === profile.insuranceProfileId
        );

        return {
            ...profile,
            profileName: match?.insuranceProfileDesc ?? undefined
            };
        });

        apptData.selectedSecondaryInsurance = apptData.insuranceProfileList?.find(payerId => payerId.insuredId === apptData.currentSecondaryInsurancePayerId);
        apptData.selectedTertiaryInsurance = apptData.insuranceProfileList?.find(payerId => payerId.insuredId === apptData.currentTertiaryInsurancePayerId);
    }

    async function loadInsuranceProfileInfo() {
        const postData = {
            patientId: -1,
            appointmentId: props.appointmentId ?? -1,
            scheduleDate: "01/01/2000",
            appointmentTypeId: "-1"
        }
        
        const resp = await ChoiceApi.schedule.InsuranceProfileInfo(postData);

        if (!resp) {
            await popAlert("There was an error pulling Insurance Profile Info", "Insurance Profile Info Error");
            return;
        }

        apptData.insuranceProfileInfo = resp;
        apptData.insuranceProfileDefault = apptData.insuranceProfileInfo.defaultResult;
        apptData.insuranceProfileList = apptData.insuranceProfileInfo.insuranceList;
        apptData.currentInsuranceProfileId = apptData.insuranceProfileDefault.insuranceProfileId; 

        apptData.currentPrimaryInsurancePayerId = props.appointmentInfo.insuranceProfilePrimaryId;
        apptData.currentSecondaryInsurancePayerId = props.appointmentInfo.insuranceProfileSecondaryId;
        apptData.currentTertiaryInsurancePayerId = props.appointmentInfo.insuranceProfileTertiaryId;      
    }

    async function loadPatientList() {        
        const postData = {
            patientEid: props.appointmentState == AppointmentState.Edit ? props.patientEntityId : undefined,  //for Edit appt, this is fixed, cannot change
            searchText: apptData.patientSearchTerm,
            displayAllResults: false   //so we only display max 50 at a time if not Edit
        }

        const resp = await ChoiceApi.patient.search(postData);

        if (!resp) {
            await popAlert("There was an error pulling patient Info", "Patient Info Error");
            return;
        }

        apptData.patientList = resp;
        apptData.selectedPatient = apptData.patientList.searchList?.find(patient => patient.entity_id === apptData.currentPatientEntityId);
    }

    async function loadResourceList() {        
        const postData = {
            resourceId: props.appointmentState == AppointmentState.Edit ? props.appointmentInfo.resourceId : undefined,  //for Edit appt, this is fixed, cannot change
            searchText: apptData.resourceSearchTerm,
            displayAllResults: false   //so we only display max 50 at a time if not Edit
        }

        const resp = await ChoiceApi.resource.search(postData);

        if (!resp) {
            await popAlert("There was an error pulling resource Info", "Resource Info Error");
            return;
        }

        apptData.resourceList = resp;
        apptData.selectedResource = apptData.resourceList.searchList?.find(resource => resource.resource_id === apptData.currentResourceId);
    }

    async function searchResources(searchText: any) {
        //only trigger a search if there is text, otherwise the selected value will not bind to anything
        if (searchText) {
            apptData.resourceSearchTerm = searchText;
            await loadResourceList();
        }        
    }

    async function searchPatients(searchText: any) {
        //only trigger a search if there is text, otherwise the selected value will not bind to anything
        if (searchText) {
            apptData.patientSearchTerm = searchText;
            await loadPatientList();
        }        
    }

    async function loadReferringProviderList() {        
        const postData = {
            searchText: apptData.providerSearchTerm,
            displayAllResults: false,   //so we only display max 50 at a time
            searchOnlyRendering: false,
            searchOnlyActive: 1
        }

        const resp = await ChoiceApi.schedule.ReferringProviderList(postData);

        if (!resp) {
            await popAlert("There was an error pulling Referring Provider Info", "Referring Provider Info Error");
            return;
        }

        apptData.referringProviderList = resp;
        apptData.selectedReferringProvider = apptData.referringProviderList?.find(entity => entity.entity_id === apptData.currentReferringProviderEntityId);
    }

    async function searchProviders(searchText: any) {
        //only trigger a search if there is text, otherwise the selected value will not bind to anything
        if (searchText) {
            apptData.providerSearchTerm = searchText;
            await loadReferringProviderList();
        }        
    }

    function openDefault(returnField: string, commentGroup: string, maxLength: number) { 
        open_default_comment_window_legacy(returnField, commentGroup, maxLength);
    }

    function formatDateTimeToCustomIso(date: Date): string {
        const pad = (n: number) => n.toString().padStart(2, '0');

        const yyyy = date.getFullYear();
        const mm = pad(date.getMonth() + 1); //Months are 0-based
        const dd = pad(date.getDate());
        const hh = pad(date.getHours());
        const min = pad(date.getMinutes());
        const sec = pad(date.getSeconds());

        return `${yyyy}-${mm}-${dd}T${hh}:${min}:${sec}`;
    }

    async function saveAppointment() {
        const postData = {
            appointment_id: props.appointmentId,                                  //for Edit appt, this is fixed, cannot change
            resource_id: apptData.selectedResource?.resource_id,                  //for Edit appt, this is fixed, cannot change
            patient_eid: apptData.selectedPatient?.entity_id,                     //for Edit appt, this is fixed, cannot change
            appointment_type_id: apptData.selectedAppointmentType?.appointmentTypeId,
            start_time: apptData.apptStartDateTime?.replace('T ', 'T'),      
            end_time: apptData.apptEndDateTime?.replace('T ', 'T'),           
            chief_complaint: apptData.chiefComplaint, 
            comment: apptData.comment,
            referring_eid: apptData.selectedReferringProvider?.entity_id ?? -1,
            prior_auth: apptData.priorAuthorizationNumber,
            service_location_id: apptData.serviceLocationId,
            force_creation: "N",
            recall_reminder_id: -1,
            patient_group_note: "",
            authorization: apptData.assignAuthorizationId?.toString(),
            insurance_profile_id: apptData.selectedInsuranceProfile?.insuranceProfileId,
            insurance_profile_primary_id: apptData.selectedPrimaryInsurance?.insuredId,
            insurance_profile_secondary_id: apptData.selectedSecondaryInsurance?.insuredId,
            insurance_profile_tertiary_id: apptData.selectedTertiaryInsurance?.insuredId
        }

        const resp = await ChoiceApi.schedule.CreateAppointment(postData);

        if (!resp) {
            await popAlert("There was an error saving the appointment", "Save Appointment Error");
            return;
        }

        showSuccessMessage();
    }

    async function loadResourceAppointmentTypeList() {        
        const resp = await ChoiceApi.schedule.ResourceAppointmentTypeList(props.appointmentInfo.resourceId);  

        if (!resp) {
            await popAlert("There was an error pulling Resource Appointment Types", "Resource Appt Type List Error");
            return;
        }

        apptData.resourceAppointmentTypeList = resp;

        //determine if there is specific appointment type list setup for this resource.  If so, replace with resource appointment type list.
        if (apptData.resourceAppointmentTypeList.length > 0) {
            apptData.appointmentTypeList = apptData.resourceAppointmentTypeList;
        }
    }

    async function updateApptType() {
        if (apptData.apptStartDateTime) {
            const startDateTime = new Date(apptData.apptStartDateTime.replace('T ', 'T'));  
            const endDateTime = addMinutes(startDateTime, apptData.selectedAppointmentType?.duration ?? 0);
            const endDateTimeFormatted = formatDateTimeToCustomIso(endDateTime);
            apptData.apptEndDateTime = endDateTimeFormatted;
        }        
    }

    async function closeWindow() {
        window.close();
    }

    function showSuccessMessage() {
        apptData.showMessage = true;
        setTimeout(() => {
            if (window.opener && !window.opener.closed) {
                window.opener.window_refresh();
            }
            closeWindow();
        }, 2000);
    }
    
</script>

<style scoped>
    div {
        font-size: 12px; 
    }

    .provider-row {
        display: grid;
        grid-template-columns: 30% 20% 20% 20% 10%;
        align-items: center;
        padding: 4px 8px;
    }

    .provider-row > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .insurance-row {
        display: grid;
        grid-template-columns: 45% 35% 20%;
        align-items: center;
        padding: 4px 8px;
    }

    .insurance-row > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .appt-type-row {
        display: grid;
        grid-template-columns: 80% 20%;
        align-items: center;
        padding: 4px 8px;
    }

    .appt-type-row > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .patient-search-row {
        display: grid;
        grid-template-columns: 15% 40% 30% 15%;
        align-items: center;
        padding: 4px 8px;
    }

    .patient-search-row > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .resource-row {
        display: grid;
        grid-template-columns: 20% 40% 40%;
        align-items: center;
        padding: 4px 8px;
    }

    .resource-row > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
</style>

<template>
    <PageTitle :security-screen-name="props.screenName">
        <template #permitted>   
            <WindowHeaderFooter :headerHeight="3.5" :footerHeight="3.5" :workareaHeight="44">
                <template v-slot:WindowHeader>
                    <div class="row mt-2">
                        <div class="col col-md-6 isalus-page-title-large pt-1">{{ ApptPageTitle }}</div>
                    </div>                    
                </template>
                <template v-slot:WindowBody>
                    <div>
                        <div class="row">
                            <div class="col mb-3 ms-2">
                                <div class="row">
                                    <div class="col-3 fw-bold">
                                        Patient<span class="red-text">*</span>
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2"                    
                                            v-model="apptData.selectedPatient"
                                            @search="searchPatients"
                                            :options="apptData.patientList?.searchList" 
                                            :searchable="true"
                                            :filterable="false"
                                            :disabled="disableSearch"
                                            label="patient_name">
                                            <template #list-header>
                                                <div class="patient-search-row">
                                                    <div class="fw-bold border background-isalus-primary-light">Chart</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Name</div>
                                                    <div class="fw-bold border background-isalus-primary-light">DOB</div>
                                                    <div class="fw-bold border background-isalus-primary-light text-center">Gender</div>
                                                </div>
                                            </template>
                                            <template #option="option: SearchModel.ISearchPatientItem">
                                                <div class="patient-search-row">
                                                    <div :title="option.patient_id.toString()">{{ option.patient_id }}</div>
                                                    <div :title="option.patient_name">{{ option.patient_name }}</div>
                                                    <div :title="option.birth_date">{{ option.birth_date }} ({{ option.age }})</div>
                                                    <div class="text-center" :title="option.gender">{{ option.gender }}</div>
                                                </div>
                                            </template>
                                        </v-select>                                               
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-3 fw-bold">
                                        Scheduled With<span class="red-text">*</span>
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2" 
                                            v-model="apptData.selectedResource"
                                            :options="apptData.resourceList?.searchList" 
                                            @search="searchResources"
                                            :searchable="true"
                                            :filterable="false"
                                            :disabled="disableSearch"
                                            label="resource_name">
                                            <template #list-header>
                                                <div class="resource-row">
                                                    <div class="fw-bold border background-isalus-primary-light">ID</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Resource Name</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Address</div>
                                                </div>
                                            </template>
                                            <template #option="option: SearchModel.ISearchResourceItem">
                                                <div class="resource-row">
                                                    <div :title="option.resource_id?.toString()">{{ option.resource_id }}</div>
                                                    <div :title="option.resource_name">{{ option.resource_name }}</div>
                                                    <div :title="option.address">{{ option.address }}</div>
                                                </div>
                                            </template>
                                        </v-select> 
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-3 fw-bold">
                                        Scheduled On<span class="red-text">*</span>
                                    </div>
                                    <div class="col-6 mb-2">
                                        <DateTimePickerNoTZ 
                                            v-model="apptData.apptStartDateTime" 
                                            :dateClass="'isalus-12px'" 
                                            :timeClass="'isalus-12px'" 
                                            :cssFramework="'vanilla'"
                                            :colClassDate="'col'"
                                            :colClassTime="'col'"
                                            :disabled="true" />
                                    </div>
                                    <div class="col-3 mb-2">
                                        <DateTimePickerNoTZ 
                                            v-model="apptData.apptEndDateTime" 
                                            :showTimeOnly="true" 
                                            :timeClass="'isalus-12px'" 
                                            :cssFramework="'vanilla'"
                                            :colClassDate="'col'"
                                            :colClassTime="'col'"
                                            :disabled="true" />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-3 fw-bold" :title="AppointmentTypeTitle">
                                        Appointment Type<span class="red-text">*</span>
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2"                    
                                            v-model="apptData.selectedAppointmentType"
                                            :options="apptData.appointmentTypeList" 
                                            :disabled="!apptData.companySettingOverridePlaceholderAppointmentType"
                                            :filterable="true"
                                            :searchable="true"
                                            label="appointmentName">
                                            <template #list-header>
                                                <div class="appt-type-row">
                                                    <div class="fw-bold border background-isalus-primary-light">Appointment Type</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Min.</div>
                                                </div>
                                            </template>
                                            <template #option="option: AppointmentModel.IAppointmentTypeItem">
                                                <div class="appt-type-row">
                                                    <div :title="option.appointmentName">{{ option.appointmentName }}</div>
                                                    <div class="text-center" :title="option.duration.toString()">{{ option.duration }}</div>
                                                </div>
                                            </template>
                                        </v-select>
                                        <div v-if="!apptData.selectedAppointmentType" class="red-text mt-0 mb-1">Required</div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-3 fw-bold" :title="ServiceLocationTitle">
                                        Location<span class="red-text" v-if="apptData.companySettingServiceLocationRequired">*</span>
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2"                    
                                            v-model="apptData.serviceLocationId"
                                            :disabled="!apptData.companySettingOverridePlaceholderServiceLocation"
                                            :options="props.lookupServiceLocations" 
                                            :reduce="item => item.serviceLocationId" label="serviceLocationName" /> 
                                        <div v-if="!apptData.serviceLocationId && apptData.companySettingServiceLocationRequired" class="red-text mt-0 mb-1">Required</div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col fw-bold">
                                        Chief Complaint
                                    </div>
                                    <div class="col text-end">
                                        <a href="javascript:void(0);" class="text-decoration-none" @click="openDefault('txtQuickComplaints', 'Appt. Complaint', 300)">Default</a>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col mb-2">
                                        <TextInput :rows="2" extraClass="isalus-12px" v-model:textValue="apptData.chiefComplaint" />
                                        <textarea id="txtQuickComplaints" name="txtQuickComplaints" ref="txtComplaintRef" class="isalus-12px" hidden>{{ apptData.chiefComplaint }}</textarea>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-4 fw-bold">
                                        Comment
                                    </div>
                                    <div class="col text-end">
                                        <a href="javascript:void(0);" class="text-decoration-none" @click="openDefault('txtQuickComments', 'Appt. Comment', 300)">Default</a>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col mb-2">
                                        <TextInput :rows="2" extraClass="isalus-12px" v-model:textValue="apptData.comment" />
                                        <textarea id="txtQuickComments" name="txtQuickComments" ref="txtCommentRef" class="isalus-12px" hidden>{{ apptData.comment }}</textarea>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-4 fw-bold">
                                        Insurance Profile<span class="red-text">*</span>
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2"                    
                                            v-model="apptData.selectedInsuranceProfile"
                                            :options="apptData.insuranceProfileLookupList" 
                                            :filterable="true"
                                            :searchable="true"
                                            label="insuranceProfileDesc">
                                            <template #list-header>
                                                <div class="row">
                                                    <div class="col fw-bold border background-isalus-primary-light text-center">Name</div>
                                                    <div class="col fw-bold border background-isalus-primary-light text-center">In Use</div>
                                                </div>
                                            </template>
                                            <template #option="option: AppointmentModel.IInsuranceProfileListItem">
                                                <div class="row">
                                                    <div class="col">{{ option.insuranceProfileDesc }}</div>
                                                    <div class="col text-center">{{ option.inUse ? "Y" : "N" }}</div>
                                                </div>
                                            </template>
                                        </v-select>  
                                        <div v-if="!apptData.selectedInsuranceProfile" class="red-text mt-0 mb-1">Required</div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-4 fw-bold">
                                        Insurance Primary
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2"                    
                                            v-model="apptData.selectedPrimaryInsurance"
                                            :options="apptData.insuranceProfileList" 
                                            :filterable="true"
                                            :searchable="true"
                                            label="payerName">
                                            <template #list-header>
                                                <div class="insurance-row">
                                                    <div class="fw-bold border background-isalus-primary-light">Payer</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Profile</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Active</div>
                                                </div>
                                            </template>
                                            <template #option="option: AppointmentModel.IInsuranceProfileItem">
                                                <div class="insurance-row">
                                                    <div :title="option.payerName">{{ option.payerName }}</div>
                                                    <div :title="option.profileName">{{ option.profileName }}</div>
                                                    <div class="text-center" :title="option.active">{{ option.active }}</div>
                                                </div>
                                            </template>
                                        </v-select> 
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-4 fw-bold">
                                        Insurance Secondary
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2"                    
                                            v-model="apptData.selectedSecondaryInsurance"
                                            :options="apptData.insuranceProfileList" 
                                            :filterable="true"
                                            :searchable="true"
                                            label="payerName">
                                            <template #list-header>
                                                <div class="insurance-row">
                                                    <div class="fw-bold border background-isalus-primary-light">Payer</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Profile</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Active</div>
                                                </div>
                                            </template>
                                            <template #option="option: AppointmentModel.IInsuranceProfileItem">
                                                <div class="insurance-row">
                                                    <div :title="option.payerName">{{ option.payerName }}</div>
                                                    <div :title="option.profileName">{{ option.profileName }}</div>
                                                    <div class="text-center" :title="option.active">{{ option.active }}</div>
                                                </div>
                                            </template>
                                        </v-select>  
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-4 fw-bold">
                                        Insurance Tertiary
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2"                    
                                            v-model="apptData.selectedTertiaryInsurance"
                                            :options="apptData.insuranceProfileList" 
                                            :filterable="true"
                                            :searchable="true"
                                            label="payerName">
                                            <template #list-header>
                                                <div class="insurance-row">
                                                    <div class="fw-bold border background-isalus-primary-light">Payer</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Profile</div>
                                                    <div class="fw-bold border background-isalus-primary-light">Active</div>
                                                </div>
                                            </template>
                                            <template #option="option: AppointmentModel.IInsuranceProfileItem">
                                                <div class="insurance-row">
                                                    <div :title="option.payerName">{{ option.payerName }}</div>
                                                    <div :title="option.profileName">{{ option.profileName }}</div>
                                                    <div class="text-center" :title="option.active">{{ option.active }}</div>
                                                </div>
                                            </template>
                                        </v-select>  
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-3 fw-bold">
                                        Referring Provider
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-2" 
                                            v-model="apptData.selectedReferringProvider"
                                            :options="apptData.referringProviderList" 
                                            @search="searchProviders"
                                            :searchable="true"
                                            :filterable="true"
                                            label="provider_name">
                                            <template #list-header>
                                                <div class="provider-row">
                                                    <div class="fw-bold border background-isalus-primary-light text-center">Name</div>
                                                    <div class="fw-bold border background-isalus-primary-light text-center">NPI</div>
                                                    <div class="fw-bold border background-isalus-primary-light text-center">Address</div>
                                                    <div class="fw-bold border background-isalus-primary-light text-center">City</div>
                                                    <div class="fw-bold border background-isalus-primary-light text-center">Active</div>
                                                </div>
                                            </template>
                                            <template #option="option: AppointmentModel.IProviderListItem">
                                                <div class="provider-row">
                                                    <div :title="option.provider_name">{{ option.provider_name }}</div>
                                                    <div :title="option.npi">{{ option.npi }}</div>
                                                    <div :title="option.address1">{{ option.address1 }}</div>
                                                    <div :title="option.city">{{ option.city }}</div>
                                                    <div class="text-center" :title="option.active == 1 ? 'Y' : 'N'">{{ option.active == 1 ? "Y" : "N" }}</div>
                                                </div>
                                            </template>
                                        </v-select> 
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-3 fw-bold">
                                        Prior Authorization #
                                    </div>
                                    <div class="col mb-2">
                                        <TextInput extraClass="isalus-12px" v-model:textValue="apptData.priorAuthorizationNumber" :maxLength="30" />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-3 fw-bold">
                                        Assign Authorization
                                    </div>
                                    <div class="col">
                                        <v-select 
                                            class="mb-1"                    
                                            v-model="apptData.assignAuthorizationId"
                                            :options="formattedAuthorizationList" 
                                            :reduce="item => item.authorizationid" 
                                            label="authorizationDesc" /> 
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="apptData.showMessage" class="text-end text-success">{{ apptData.saveMessageText }}</div>
                </template>
                <template v-slot:WindowFooter>
                    <div class="row">
                        <div class="col text-end">
                            <span class="pe-2">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Save"
                                    :text="'Save'"
                                    :size="ButtonDisplaySize.Small" 
                                    :disabled="disableSaveButton"
                                    :spinText="'Saving...'"
                                    :performAction="saveAppointment"
                                />
                            </span> 
                            <span class="pe-2">
                                <ActionButtonV2 
                                    :action="ActionButtonStyle.Close"
                                    :text="'Cancel'"
                                    :size="ButtonDisplaySize.Small" 
                                    :spinText="'Closing'"
                                    :performAction="closeWindow"
                                />
                            </span>        
                        </div> 
                    </div>
                </template> 
            </WindowHeaderFooter> 
        </template>
    </PageTitle>
</template>
